import { forwardRef, useEffect, useMemo, useState } from 'react';
import { Divider, Space, Typography } from 'antd';
import { useFormik, yupToFormErrors } from 'formik';
import DeclarationInput from 'components/ui/composed/declarations/formInput';
import DeclarationSelect from 'components/ui/composed/declarations/formSelect';
import FormTextArea from 'components/new-ui/composed/formTextArea/FormTextArea';
import { H3 } from 'components/new-ui/base/typography/styles';
import { ICS2CodelistsMap } from 'store/codelists/code';
import { DeclarationTypes } from 'store/declarations/enums/common/declaration-types';
import {
    ICS2_ATTACHMENT_FILE_TYPES,
    ICS2_MAX_ATTACHMENTS_PER_REFERRAL,
    isSelectableIcs2Document,
} from 'store/file-upload/ics2';
import AttachmentsField from '../../AttachmentsField';
import FormField from '../../FormField';
import { ResponseFormHandle } from '../../responseForm';
import useResponseFormHandle from '../../useResponseFormHandle';
import { describeDocumentRequest } from '../evidenceExpectation';
import { ResponseFormProps } from '../responseForm';
import usePostcodeOptionalCountries from '../hooks/usePostcodeOptionalCountries';
import OptionalSection from './OptionalSection';
import { validationSchema } from './validationSchema';
import { FormFields, FormValues, initialFormValues, toReferralDraft } from './types';

type Props = ResponseFormProps<ReturnType<typeof toReferralDraft>>;

const codelistSelect = (codelistName: string) => ({
    condensed: true,
    hideCodelistMenu: true,
    codeValidation: [],
    codelistProps: { codelistName, declarationType: DeclarationTypes.ICS2 as const },
});

const ResponseForm = forwardRef<ResponseFormHandle, Props>(
    ({ referral, attachments, disabled = false, onSubmit, onSave }, ref) => {
        const [isPersonExpanded, setIsPersonExpanded] = useState(false);
        const [isFacilityExpanded, setIsFacilityExpanded] = useState(false);
        const postcodeOptionalCountries = usePostcodeOptionalCountries();

        const initialValues = useMemo<FormValues>(() => initialFormValues(referral), [referral]);

        const formik = useFormik<FormValues>({
            initialValues,
            enableReinitialize: true,
            validate: (values) => {
                try {
                    validationSchema.validateSync(values, {
                        abortEarly: false,
                        context: { postcodeOptionalCountries },
                    });
                    return {};
                } catch (error) {
                    return yupToFormErrors(error);
                }
            },
            onSubmit: async (values) => {
                await onSubmit?.(toReferralDraft(values));
            },
        });

        useResponseFormHandle(ref, {
            formik,
            toDraft: toReferralDraft,
            onSave,
            attachmentErrors: attachments.attachmentErrors,
        });

        // An incomplete optional block is easy to miss while it is collapsed.
        useEffect(() => {
            if (formik.errors.authorizedPerson) setIsPersonExpanded(true);
        }, [formik.errors.authorizedPerson]);

        useEffect(() => {
            if (formik.errors.facilityAddress) setIsFacilityExpanded(true);
        }, [formik.errors.facilityAddress]);

        const hasPerson = Object.values(formik.values.authorizedPerson).some((value) => value?.trim());

        useEffect(() => {
            if (disabled && hasPerson) setIsPersonExpanded(true);
        }, [disabled, hasPerson]);

        const hasFacility = Object.values(formik.values.facilityAddress).some((value) => value?.trim());
        useEffect(() => {
            if (disabled && hasFacility) setIsFacilityExpanded(true);
        }, [disabled, hasFacility]);

        // Every control on this form is spread from here, so read-only is carried by the same
        // helper rather than repeated on each of the fourteen fields.
        const field = (path: string) => ({
            fieldProps: formik.getFieldProps(path),
            fieldHelper: formik.getFieldHelpers(path),
            fieldMeta: formik.getFieldMeta(path),
            disabled,
        });

        return (
            <form onSubmit={formik.handleSubmit}>
                <section>
                    <Space size={16} direction="vertical" style={{ width: '100%' }}>
                        <H3>Screening Details</H3>

                        <FormField
                            required
                            label="Screening Result"
                            htmlFor={`select-id-${FormFields.SCREENING_DETAILS.RESULT}`}
                        >
                            <DeclarationSelect
                                {...field(FormFields.SCREENING_DETAILS.RESULT)}
                                {...codelistSelect(ICS2CodelistsMap.CL726)}
                                placeholder="Select Screening Result"
                            />
                        </FormField>

                        <FormField
                            required
                            label="Screening Methods (min. 2)"
                            htmlFor={`select-id-${FormFields.SCREENING_DETAILS.METHODS}`}
                            hint='Customs expects at least two methods. Use "Only one HRCM screening method required" where a single method was genuinely sufficient.'
                        >
                            <DeclarationSelect
                                {...field(FormFields.SCREENING_DETAILS.METHODS)}
                                {...codelistSelect(ICS2CodelistsMap.CL724)}
                                mode="multiple"
                                placeholder="Select Screening Methods"
                            />
                        </FormField>

                        <FormField
                            label={
                                <>
                                    Response Code <span style={{ fontWeight: 400 }}>(optional)</span>
                                </>
                            }
                            htmlFor={`select-id-${FormFields.SCREENING_DETAILS.CODE}`}
                        >
                            <DeclarationSelect
                                {...field(FormFields.SCREENING_DETAILS.CODE)}
                                {...codelistSelect(ICS2CodelistsMap.CL752)}
                                allowClear
                                placeholder="Select Response Code"
                            />
                        </FormField>

                        <FormField label="Additional Notes" htmlFor={FormFields.SCREENING_DETAILS.NOTES}>
                            <FormTextArea
                                id={FormFields.SCREENING_DETAILS.NOTES}
                                {...field(FormFields.SCREENING_DETAILS.NOTES)}
                                placeholder="Max 512 chars"
                            />
                        </FormField>
                    </Space>
                </section>

                <Divider />

                <OptionalSection
                    title="Authorised Person Details"
                    isExpanded={isPersonExpanded}
                    onToggle={() => setIsPersonExpanded(!isPersonExpanded)}
                >
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        If you name the person who authorised the screening, all three fields are required.
                    </Typography.Text>

                    <FormField label="Type" htmlFor={`select-id-${FormFields.AUTHORIZED_PERSON.TYPE}`}>
                        <DeclarationSelect
                            {...field(FormFields.AUTHORIZED_PERSON.TYPE)}
                            {...codelistSelect(ICS2CodelistsMap.CL729)}
                            allowClear
                            placeholder="Select Type"
                        />
                    </FormField>

                    <FormField label="Name" htmlFor={`select-id-${FormFields.AUTHORIZED_PERSON.NAME}`}>
                        <DeclarationInput
                            {...field(FormFields.AUTHORIZED_PERSON.NAME)}
                            placeholder="Enter Name"
                            condensed
                        />
                    </FormField>

                    <FormField label="ID" htmlFor={`select-id-${FormFields.AUTHORIZED_PERSON.ID}`}>
                        <DeclarationInput
                            {...field(FormFields.AUTHORIZED_PERSON.ID)}
                            placeholder="Enter ID"
                            condensed
                        />
                    </FormField>
                </OptionalSection>

                <Divider />

                <OptionalSection
                    title="Screening Facility Details"
                    isExpanded={isFacilityExpanded}
                    onToggle={() => setIsFacilityExpanded(!isFacilityExpanded)}
                >
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        Give either a street and number, or a P.O. box. City and country are required once any part of
                        the address is filled in.
                    </Typography.Text>

                    <FormField label="Street" htmlFor={`select-id-${FormFields.FACILITY.STREET}`}>
                        <DeclarationInput {...field(FormFields.FACILITY.STREET)} placeholder="Enter Street" condensed />
                    </FormField>

                    <FormField label="Number" htmlFor={`select-id-${FormFields.FACILITY.NUMBER}`}>
                        <DeclarationInput {...field(FormFields.FACILITY.NUMBER)} placeholder="Enter Number" condensed />
                    </FormField>

                    <FormField label="P.O. Box" htmlFor={`select-id-${FormFields.FACILITY.PO_BOX}`}>
                        <DeclarationInput
                            {...field(FormFields.FACILITY.PO_BOX)}
                            placeholder="Enter P.O. Box"
                            condensed
                        />
                    </FormField>

                    <FormField label="City" htmlFor={`select-id-${FormFields.FACILITY.CITY}`}>
                        <DeclarationInput {...field(FormFields.FACILITY.CITY)} placeholder="Enter City" condensed />
                    </FormField>

                    <FormField label="Postcode" htmlFor={`select-id-${FormFields.FACILITY.POST_CODE}`}>
                        <DeclarationInput
                            {...field(FormFields.FACILITY.POST_CODE)}
                            placeholder="Enter Postcode"
                            condensed
                        />
                    </FormField>

                    <FormField label="Country" htmlFor={`select-id-${FormFields.FACILITY.COUNTRY}`}>
                        <DeclarationSelect
                            {...field(FormFields.FACILITY.COUNTRY)}
                            {...codelistSelect(ICS2CodelistsMap.CL718)}
                            allowClear
                            placeholder="Select Country"
                            getPopupContainer={() => document.body}
                        />
                    </FormField>
                </OptionalSection>

                <Divider />

                <AttachmentsField
                    acceptedTypes={ICS2_ATTACHMENT_FILE_TYPES}
                    maxCount={ICS2_MAX_ATTACHMENTS_PER_REFERRAL}
                    hint="Attach X-ray scans, ETD certificates, screening reports or other evidence."
                    requested={describeDocumentRequest(referral)}
                    disabled={disabled}
                    documents={attachments.documents}
                    isLoading={attachments.isLoading}
                    isUploading={attachments.isUploading}
                    canAddMore={attachments.canAddMore}
                    isRemovable={isSelectableIcs2Document}
                    onUpload={attachments.upload}
                    onRemove={attachments.remove}
                    onDownload={attachments.download}
                    onPreview={attachments.preview}
                />
            </form>
        );
    }
);

export default ResponseForm;
